// DNA CONNECT - DNA GO (Família)
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { 
  Car, Users, Clock, MapPin, CheckCircle, Loader2,
  Bell, History, UserPlus, Phone, AlertTriangle, Navigation, MapPinOff
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = window.location.origin;

// Coordenadas da escola NOVO DNA
const ESCOLA_COORDS = {
  latitude: -23.618248338400015,
  longitude: -46.79031908493496,
  nome: "NOVO DNA - Muitas mentes, uma missão!"
};

// Distância máxima permitida em metros
const DISTANCIA_MAXIMA = 300;

// Função para calcular distância entre dois pontos (fórmula de Haversine)
function calcularDistancia(lat1, lon1, lat2, lon2) {
  const R = 6371e3; // Raio da Terra em metros
  const φ1 = lat1 * Math.PI / 180;
  const φ2 = lat2 * Math.PI / 180;
  const Δφ = (lat2 - lat1) * Math.PI / 180;
  const Δλ = (lon2 - lon1) * Math.PI / 180;

  const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
            Math.cos(φ1) * Math.cos(φ2) *
            Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c; // Distância em metros
}

export default function DnaConnectDnaGo() {
  const [loading, setLoading] = useState(true);
  const [filhos, setFilhos] = useState([]);
  const [autorizados, setAutorizados] = useState([]);
  const [historico, setHistorico] = useState([]);
  const [activeTab, setActiveTab] = useState('chegando');
  const [selectedFilho, setSelectedFilho] = useState(null);
  const [confirming, setConfirming] = useState(false);
  
  // Estados de geolocalização
  const [verificandoLocalizacao, setVerificandoLocalizacao] = useState(false);
  const [localizacaoStatus, setLocalizacaoStatus] = useState(null); // 'permitido', 'longe', 'erro', 'sem_permissao'
  const [distanciaAtual, setDistanciaAtual] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('familyToken');
      
      const results = await Promise.allSettled([
        fetch(`${API_URL}/api/dnaconnect/filhos?token=${token}`).then(r => r.ok ? r.json() : []),
        fetch(`${API_URL}/api/dnaconnect/dnago/autorizados?token=${token}`).then(r => r.ok ? r.json() : []),
        fetch(`${API_URL}/api/dnaconnect/dnago/historico?token=${token}`).then(r => r.ok ? r.json() : [])
      ]);
      
      setFilhos(results[0].status === 'fulfilled' ? (Array.isArray(results[0].value) ? results[0].value : []) : []);
      setAutorizados(results[1].status === 'fulfilled' ? (Array.isArray(results[1].value) ? results[1].value : []) : []);
      setHistorico(results[2].status === 'fulfilled' ? (Array.isArray(results[2].value) ? results[2].value : []) : []);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  // Verificar localização do usuário
  const verificarLocalizacao = () => {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        resolve({ status: 'erro', mensagem: 'Seu navegador não suporta geolocalização' });
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          const distancia = calcularDistancia(
            latitude, 
            longitude, 
            ESCOLA_COORDS.latitude, 
            ESCOLA_COORDS.longitude
          );
          
          setDistanciaAtual(Math.round(distancia));
          
          if (distancia <= DISTANCIA_MAXIMA) {
            resolve({ status: 'permitido', distancia: Math.round(distancia) });
          } else {
            resolve({ status: 'longe', distancia: Math.round(distancia) });
          }
        },
        (error) => {
          console.error('Erro de geolocalização:', error);
          switch (error.code) {
            case error.PERMISSION_DENIED:
              resolve({ status: 'sem_permissao', mensagem: 'Permissão de localização negada' });
              break;
            case error.POSITION_UNAVAILABLE:
              resolve({ status: 'erro', mensagem: 'Localização indisponível' });
              break;
            case error.TIMEOUT:
              resolve({ status: 'erro', mensagem: 'Tempo esgotado ao obter localização' });
              break;
            default:
              resolve({ status: 'erro', mensagem: 'Erro ao obter localização' });
          }
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        }
      );
    });
  };

  // Handler para o botão "Avisar"
  const handleAvisarClick = async (filho) => {
    setSelectedFilho(filho);
    setVerificandoLocalizacao(true);
    setLocalizacaoStatus(null);
    
    const resultado = await verificarLocalizacao();
    
    setVerificandoLocalizacao(false);
    setLocalizacaoStatus(resultado.status);
    
    if (resultado.status === 'permitido') {
      // Localização OK, permitir enviar
      toast.success(`Você está a ${resultado.distancia}m da escola!`);
    } else if (resultado.status === 'longe') {
      toast.error(`Você está a ${resultado.distancia}m da escola. Aproxime-se para avisar (máx. ${DISTANCIA_MAXIMA}m).`);
    } else if (resultado.status === 'sem_permissao') {
      toast.error('Por favor, permita o acesso à sua localização nas configurações do navegador.');
    } else {
      toast.error(resultado.mensagem || 'Erro ao verificar localização');
    }
  };

  const handleEstouChegando = async () => {
    if (!selectedFilho) return;
    
    // Verificar localização novamente antes de enviar
    setVerificandoLocalizacao(true);
    const resultado = await verificarLocalizacao();
    setVerificandoLocalizacao(false);
    
    if (resultado.status !== 'permitido') {
      toast.error(`Você precisa estar a no máximo ${DISTANCIA_MAXIMA}m da escola para avisar.`);
      setLocalizacaoStatus(resultado.status);
      return;
    }
    
    setConfirming(true);
    try {
      const token = localStorage.getItem('familyToken');
      const response = await fetch(
        `${API_URL}/api/dnaconnect/dnago/estou-chegando/${selectedFilho.id}?token=${token}`,
        { method: 'POST' }
      );
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(data.message || 'Notificação enviada! O aluno será chamado.');
        setSelectedFilho(null);
        setLocalizacaoStatus(null);
      } else {
        toast.error(data.detail || 'Erro ao enviar notificação');
      }
    } catch (error) {
      toast.error('Erro ao enviar notificação');
    } finally {
      setConfirming(false);
    }
  };

  const handleCancelar = () => {
    setSelectedFilho(null);
    setLocalizacaoStatus(null);
    setDistanciaAtual(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center">
          <Car className="w-6 h-6 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-800">DNA GO</h1>
          <p className="text-gray-500">Retirada segura de alunos</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="chegando" className="gap-2">
            <Bell className="w-4 h-4" />
            Estou Chegando
          </TabsTrigger>
          <TabsTrigger value="autorizados" className="gap-2">
            <Users className="w-4 h-4" />
            Autorizados
          </TabsTrigger>
          <TabsTrigger value="historico" className="gap-2">
            <History className="w-4 h-4" />
            Histórico
          </TabsTrigger>
        </TabsList>

        {/* Estou Chegando */}
        <TabsContent value="chegando" className="space-y-4">
          <Card className="bg-gradient-to-r from-orange-50 to-amber-50 border-orange-200">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-orange-500 rounded-xl flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="font-bold text-orange-900 mb-2">Avise que está chegando!</h2>
                  <p className="text-orange-700 text-sm">
                    Ao selecionar seu filho, ele será adicionado à fila de retirada 
                    e chamado assim que você chegar na escola.
                  </p>
                  <div className="mt-3 flex items-center gap-2 text-orange-600 text-sm">
                    <Navigation className="w-4 h-4" />
                    <span>Você precisa estar a no máximo <strong>{DISTANCIA_MAXIMA}m</strong> da escola para avisar.</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="grid gap-4">
            {filhos.map((filho) => (
              <Card 
                key={filho.id}
                className="cursor-pointer hover:shadow-md transition-all"
                onClick={() => handleAvisarClick(filho)}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 rounded-xl overflow-hidden">
                        <img src={filho.photo_url || "/default-avatar.png"} onError={(e) => { e.currentTarget.src = "/default-avatar.png"; }} alt="" className="w-14 h-14 rounded-xl object-cover" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{filho.name}</h3>
                        <p className="text-sm text-gray-500">{filho.class_name}</p>
                      </div>
                    </div>
                    
                    <Button className="bg-orange-500 hover:bg-orange-600">
                      <Navigation className="w-4 h-4 mr-2" />
                      Avisar
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Autorizados */}
        <TabsContent value="autorizados" className="space-y-4">
          {autorizados.length === 0 ? (
            <Card className="text-center py-12">
              <CardContent>
                <Users className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500 mb-4">Nenhuma pessoa autorizada cadastrada</p>
                <p className="text-sm text-gray-400">
                  Entre em contato com a escola para adicionar pessoas autorizadas.
                </p>
              </CardContent>
            </Card>
          ) : (
            autorizados.map((autorizado) => (
              <Card key={autorizado.id}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center">
                      <Users className="w-7 h-7 text-green-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900">{autorizado.nome}</h3>
                      <p className="text-sm text-gray-500">
                        {autorizado.grau_parentesco} • {autorizado.telefone}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        {autorizado.face_template ? (
                          <Badge className="bg-green-100 text-green-700">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Biometria OK
                          </Badge>
                        ) : (
                          <Badge className="bg-amber-100 text-amber-700">
                            <AlertTriangle className="w-3 h-3 mr-1" />
                            Sem biometria
                          </Badge>
                        )}
                        {autorizado.ativo ? (
                          <Badge className="bg-green-100 text-green-700">Ativo</Badge>
                        ) : (
                          <Badge variant="secondary">Inativo</Badge>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>

        {/* Histórico */}
        <TabsContent value="historico" className="space-y-4">
          {historico.length === 0 ? (
            <Card className="text-center py-12">
              <CardContent>
                <History className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500">Nenhuma retirada registrada</p>
              </CardContent>
            </Card>
          ) : (
            historico.map((retirada, idx) => (
              <Card key={idx}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                      <CheckCircle className="w-6 h-6 text-purple-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900">{retirada.aluno_nome}</h3>
                      <p className="text-sm text-gray-500">
                        Retirado por: {retirada.autorizado_nome || 'Responsável'}
                      </p>
                      <div className="flex items-center gap-2 mt-1 text-xs text-gray-400">
                        <Clock className="w-3 h-3" />
                        {new Date(retirada.hora_retirada).toLocaleString('pt-BR')}
                      </div>
                    </div>
                    <Badge variant={retirada.tipo === 'normal' ? 'default' : 'secondary'}>
                      {retirada.tipo === 'van' ? 'Van' : 'Normal'}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>
      </Tabs>

      {/* Dialog de Confirmação com Status de Localização */}
      <Dialog open={!!selectedFilho} onOpenChange={handleCancelar}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Confirmar Aviso de Chegada</DialogTitle>
          </DialogHeader>
          
          {selectedFilho && (
            <div className="py-4 space-y-4">
              <div className="flex items-center gap-4 p-4 bg-purple-50 rounded-xl">
                <div className="w-16 h-16 rounded-xl overflow-hidden">
                  <img src={selectedFilho.photo_url || "/default-avatar.png"} onError={(e) => { e.currentTarget.src = "/default-avatar.png"; }} alt="" className="w-16 h-16 object-cover" />
                </div>
                <div>
                  <h3 className="font-bold text-purple-900">{selectedFilho.name}</h3>
                  <p className="text-purple-700">{selectedFilho.class_name}</p>
                </div>
              </div>
              
              {/* Status de Localização */}
              <div className="rounded-xl border-2 p-4" data-testid="geolocation-status">
                {verificandoLocalizacao ? (
                  <div className="flex items-center gap-3 text-blue-600">
                    <Loader2 className="w-6 h-6 animate-spin" />
                    <div>
                      <p className="font-semibold">Verificando sua localização...</p>
                      <p className="text-sm text-blue-500">Por favor, aguarde</p>
                    </div>
                  </div>
                ) : localizacaoStatus === 'permitido' ? (
                  <div className="flex items-center gap-3 text-green-600 bg-green-50 -m-4 p-4 rounded-xl border-green-200 border-2">
                    <CheckCircle className="w-8 h-8" />
                    <div>
                      <p className="font-semibold">Localização Confirmada!</p>
                      <p className="text-sm text-green-500">
                        Você está a <strong>{distanciaAtual}m</strong> da escola
                      </p>
                    </div>
                  </div>
                ) : localizacaoStatus === 'longe' ? (
                  <div className="flex items-center gap-3 text-red-600 bg-red-50 -m-4 p-4 rounded-xl border-red-200 border-2">
                    <MapPinOff className="w-8 h-8" />
                    <div>
                      <p className="font-semibold">Você está muito longe!</p>
                      <p className="text-sm text-red-500">
                        Distância atual: <strong>{distanciaAtual}m</strong> (máx. {DISTANCIA_MAXIMA}m)
                      </p>
                    </div>
                  </div>
                ) : localizacaoStatus === 'sem_permissao' ? (
                  <div className="flex items-center gap-3 text-amber-600 bg-amber-50 -m-4 p-4 rounded-xl border-amber-200 border-2">
                    <AlertTriangle className="w-8 h-8" />
                    <div>
                      <p className="font-semibold">Permissão Negada</p>
                      <p className="text-sm text-amber-500">
                        Ative a localização nas configurações do navegador
                      </p>
                    </div>
                  </div>
                ) : localizacaoStatus === 'erro' ? (
                  <div className="flex items-center gap-3 text-gray-600 bg-gray-50 -m-4 p-4 rounded-xl border-gray-200 border-2">
                    <AlertTriangle className="w-8 h-8" />
                    <div>
                      <p className="font-semibold">Erro ao obter localização</p>
                      <p className="text-sm text-gray-500">
                        Verifique se o GPS está ativado e tente novamente
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-3 text-gray-500">
                    <MapPin className="w-6 h-6" />
                    <div>
                      <p className="font-medium">Aguardando verificação de localização...</p>
                      <p className="text-sm text-gray-400">
                        Precisamos confirmar que você está próximo da escola
                      </p>
                    </div>
                  </div>
                )}
              </div>
              
              {localizacaoStatus === 'permitido' && (
                <p className="text-gray-600 text-center text-sm">
                  Ao confirmar, a escola será notificada e seu filho será adicionado à fila de retirada.
                </p>
              )}
            </div>
          )}
          
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={handleCancelar}>
              Cancelar
            </Button>
            <Button 
              onClick={handleEstouChegando}
              disabled={confirming || verificandoLocalizacao || localizacaoStatus !== 'permitido'}
              className="bg-orange-500 hover:bg-orange-600 disabled:opacity-50"
              data-testid="confirm-chegando-btn"
            >
              {confirming ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Bell className="w-4 h-4 mr-2" />
              )}
              Estou Chegando!
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
